import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { SeverityPills } from '@shape/lighthouse-components';
import '../StatusCountPanel/style.scss';
import { useFeatureFlag } from '@shape/feature-flag';
export const severityMap: { [key: string]: SeverityNumber } = {
  Information: 1,
  Warning: 1,
  Minor: 2,
  Major: 3,
  Critical: 4,
};
export const conditionEvaluationMap: SeverityMap = {
  NORMAL_CONDITION: 1,
  ALARM_CONDITION: 2,
  POTENTIAL_PROBLEM: 3,
  CRITICAL_CONDITION: 4,
};
export const conditionEvaluationModecMap: SeverityMap = {
  NORMAL_CONDITION: 0,
  NOT_OPERATED: 1,
  NOT_MONITORED: 2,
  ALARM_CONDITION: 3,
  POTENTIAL_PROBLEM: 4,
  CRITICAL_CONDITION: 5,
};

export const severityMaps: { [key in 'severity' | 'condition_evaluation']: SeverityMap } = {
  severity: severityMap,
  condition_evaluation: conditionEvaluationMap,
};

export const SeverityPanel = ({
  statusCounts,
  selectedSeverity,
  getLabel,
  onClick,
  severityKey,
  showPills = true,
  isInPdfReport,
  keyMap,
}: SeverityPanelProps) => {
  const { t } = useTranslation();
  const isModec = useFeatureFlag('is-modec');

  const hasFivePills = isModec && severityKey === 'condition_evaluation';
  const length = hasFivePills ? 6 : 4;
  const severityNumbers = new Array(length)
    .fill(0)
    .map((_, index) => (length === 6 ? index : index + 1))
    .reverse();
  const statusNames = Object.keys(keyMap) ?? [];

  const formattedStatusCounts = useMemo(
    () =>
      statusNames.reduce((acc, item, index) => {
        const prevItem = statusNames[index - 1];
        const prevItemValue = keyMap[prevItem];
        if (keyMap[item] === index && prevItemValue === index) {
          return {
            ...acc,
            [prevItem]: Number(acc[prevItem] ?? 0) + Number(statusCounts[item] ?? 0),
          };
        }

        return { ...acc, [item]: statusCounts[item] };
      }, {} as { [key: string]: number }),
    [statusCounts],
  );

  return (
    <div className={`severity-box `}>
      <div className="box-title">{getLabel('title')}</div>
      <div className="severity-items">
        {isInPdfReport && (
          <div className="box-pdf-cd-wrapper">
            <div className="box-pdf-divider">
              <strong className="box-value">{statusCounts['TOTAL']}</strong>
              <div className="box-caption">
                {t('event_page:status_count_panel.status.total.title')}
              </div>
            </div>
          </div>
        )}
        {severityNumbers.map((number) => {
          const item = Object.keys(formattedStatusCounts)[hasFivePills ? number : number - 1];
          const severityNumber = number;

          return (
            <div
              className={`severity-outer-item ${item} ${
                selectedSeverity?.includes(item) ? 'severity-active' : ''
              }`}
              key={item}
              onClick={() => {
                if (item === 'Warning' || item === 'Information') {
                  return onClick?.(['Warning', 'Information']);
                }
                return onClick?.([item]);
              }}
            >
              <div className="severity-item">
                <strong className="box-value">{formattedStatusCounts[item] ?? 0}</strong>
                {showPills && (
                  <SeverityPills severity={severityNumber} totalPills={hasFivePills ? 5 : 4} />
                )}
              </div>
              <div className="box-caption">
                {getLabel(`${hasFivePills ? 'modec.' : ''}${number}`)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
